<template>
  <div class="ship-monitor-screen">
    <div class="map-layer">
      <ship-map-view ref="shipMapView"></ship-map-view>
    </div>

    <div class="overlay-layer">
      <div class="screen-header">
        <div class="header-title">{{ `${titleBridgeName}防撞主动预警系统` }}</div>
        <div
          class="header-subline"
          v-loading="loadingState.headerWeather"
          element-loading-background="rgba(9, 25, 58, 0.35)"
          element-loading-custom-class="panel-loading-mask"
        >
          <div class="header-subline-track">
            <span class="header-item"> {{ nowTime }}</span>
            <span class="header-divider"></span>
            <span class="header-item">净空高 {{ headerWeather.waterLevel }} m</span>
            <span class="header-divider"></span>
            <span class="header-item">能见度 {{ headerWeather.visibility }} km</span>
            <span class="header-divider"></span>
            <span class="header-item">风速 {{ headerWeather.windSpeed }} m/s</span>
            <span class="header-divider"></span>
            <span class="header-item">风向 {{ headerWeather.windDirection }}</span>
          </div>
        </div>
      </div>

      <div class="screen-main">
        <div class="side-panel left-panel">
          <div class="panel-card stats-card">
            <div class="card-title">船舶概览（今日）</div>
            <div
              class="stats-grid"
              v-loading="dashboardLoading"
              element-loading-background="rgba(9, 25, 58, 0.35)"
              element-loading-custom-class="panel-loading-mask"
            >
              <div class="stat-item">
                <div class="stat-label">通航总数</div>
                <div class="stat-value">{{ dashboard.todayNavigationCount }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">预警总数</div>
                <div class="stat-value">{{ dashboard.todayAlertCount }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">偏航</div>
                <div class="stat-value">{{ dashboard.todayDeviationCount }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">超高</div>
                <div class="stat-value">{{ dashboard.todayOverheightCount }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">超速</div>
                <div class="stat-value">{{ dashboard.todayOverspeedCount }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">滞留</div>
                <div class="stat-value">{{ dashboard.todayDetentionCount }}</div>
              </div>
            </div>
          </div>

          <div class="panel-card list-card">
            <div class="card-title">水文气象</div>
            <div
              class="hydro-body"
              v-loading="loadingState.hydrometeorological"
              element-loading-background="rgba(9, 25, 58, 0.35)"
              element-loading-custom-class="panel-loading-mask"
            >
              <div v-for="(item, index) in hydrometeorologicalList" :key="`hydro-${item.type}-${index}`" class="hydro-item">
                <div class="hydro-icon" :class="`${item.type}-icon`">
                  <i :class="item.icon"></i>
                </div>
                <div class="hydro-content">
                  <div class="hydro-label">{{ item.label }}</div>
                  <div class="hydro-values">
                    <span class="hydro-value">{{ item.primaryValue }}</span>
                    <span v-if="item.secondaryValue" class="hydro-value">{{ item.secondaryValue }}</span>
                  </div>
                </div>
                <div class="hydro-status" :class="item.statusClass">
                  <span class="status-dot"></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="side-panel right-panel">
          <div class="panel-card list-card">
            <div class="card-title">设备信息</div>
            <div
              class="list-body"
              v-loading="loadingState.device"
              element-loading-background="rgba(9, 25, 58, 0.35)"
              element-loading-custom-class="panel-loading-mask"
            >
              <div class="list-head list-row device-list-row">
                <span class="row-name">名称</span>
                <span class="row-type">类型</span>
                <span class="row-time">位置</span>
                <span class="row-status">状态</span>
              </div>
              <div v-for="(item, index) in displayDeviceList" :key="`device-${item.name}-${item.type}-${index}`" class="list-row device-list-row">
                <span class="row-name" :title="item.name">{{ item.name }}</span>
                <span class="row-type" :title="item.type">{{ item.type }}</span>
                <span class="row-time" :title="item.location">{{ item.location }}</span>
                <span class="row-status">
                  <el-tag size="mini" :type="item.statusType">{{ item.statusText }}</el-tag>
                </span>
              </div>
              <div v-if="!deviceList.length" class="empty-text">暂无设备数据</div>
            </div>
          </div>

          <div class="panel-card list-card">
            <div class="card-title">设备在线率</div>
            <div
              class="online-rate-body"
              v-loading="loadingState.device"
              element-loading-background="rgba(9, 25, 58, 0.35)"
              element-loading-custom-class="panel-loading-mask"
            >
              <div class="online-rate-top-stats">
                <div class="online-rate-top-item">
                  <div class="top-item-label">设备总数</div>
                  <div class="top-item-value">{{ deviceOnlineStats.total }}</div>
                </div>
                <div class="online-rate-top-item">
                  <div class="top-item-label">在线数</div>
                  <div class="top-item-value online">{{ deviceOnlineStats.online }}</div>
                </div>
                <div class="online-rate-top-item">
                  <div class="top-item-label">离线数</div>
                  <div class="top-item-value offline">{{ deviceOnlineStats.offline }}</div>
                </div>
              </div>
              <div ref="onlineRateMain" class="online-rate-main">
                <div class="online-rate-chart-wrap" :style="onlineRateChartWrapStyle">
                  <el-progress
                    class="online-rate-progress"
                    type="circle"
                    :percentage="onlineRatePercent"
                    :width="onlineRateProgressSize"
                    :stroke-width="onlineRateCircleStrokeWidth"
                    :color="onlineRateProgressColor"
                    :show-text="false"
                  ></el-progress>
                  <div class="online-rate-progress-center" :style="onlineRateCenterWrapStyle">
                    <div class="online-rate-value" :style="onlineRateCenterValueStyle">{{ deviceOnlineStats.onlineRate }}%</div>
                    <div class="online-rate-label" :style="onlineRateCenterLabelStyle">在线率</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="screen-bottom">
        <div class="bottom-card">
          <div class="card-title">航舶记录</div>
          <div
            ref="planScroll"
            class="table-wrap"
            v-loading="loadingState.crossing"
            element-loading-background="rgba(9, 25, 58, 0.35)"
            element-loading-custom-class="panel-loading-mask"
            @mouseenter="handleScrollMouseEnter('plan')"
            @mouseleave="handleScrollMouseLeave('plan')"
          >
            <table>
              <thead>
                <tr>
                  <th>船舶MMSI</th>
                  <th>船舶名称</th>
                  <th>时间</th>
                  <th>桥梁名称</th>
                  <th>航速(节)</th>
                  <th>行进方向</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in displayPlanList" :key="'plan-' + item.id + '-' + index">
                  <td :title="item.vesselMmsi">{{ item.vesselMmsi }}</td>
                  <td :title="item.vesselName">{{ item.vesselName }}</td>
                  <td :title="item.eventTime">{{ item.eventTime }}</td>
                  <td :title="item.bridgeName">{{ item.bridgeName }}</td>
                  <td :title="item.speedDisplay">{{ item.speedDisplay }}</td>
                  <td>
                    <el-tag size="mini" :type="getDirectionTagType(item.approachDirection)">
                      {{ getDirectionText(item.approachDirection, item.approachDirectionName) }}
                    </el-tag>
                  </td>
                  <td>
                    <el-button
                      size="mini"
                      type="success"
                      :disabled="!item.vesselMmsi || item.vesselMmsi === '-'"
                      @click="showPlanShipTrack(item)"
                    >
                      轨迹
                    </el-button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="!crossingList.length" class="empty-text">暂无航舶记录数据</div>
          </div>
        </div>
        <div class="bottom-card">
          <div class="card-title">预警记录</div>
          <div
            ref="dutyScroll"
            class="table-wrap"
            v-loading="loadingState.alert"
            element-loading-background="rgba(9, 25, 58, 0.35)"
            element-loading-custom-class="panel-loading-mask"
            @mouseenter="handleScrollMouseEnter('duty')"
            @mouseleave="handleScrollMouseLeave('duty')"
          >
            <table>
              <thead>
                <tr>
                  <th>MMSI</th>
                  <th>级别</th>
                  <th>类型</th>
                  <th>状态</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in displayWarningList" :key="'warning-' + item.id + '-' + index">
                  <td :title="item.sourceId">{{ item.sourceId }}</td>
                  <td :title="item.alertLevelText">{{ item.alertLevelText }}</td>
                  <td :title="item.ruleType">{{ item.ruleType }}</td>
                  <td>
                    <el-tag :type="getAlertStatusType(item.status)" size="mini">{{ item.statusText }}</el-tag>
                  </td>
                  <td :title="item.alertTime">{{ item.alertTime }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="!alertList.length" class="empty-text">暂无预警记录数据</div>
          </div>
        </div>
      </div>
      <div class="bottom-safe-gap"></div>
    </div>
  </div>
</template>

<script>
import ShipMapView from '@/components/maritime/ShipMapView.vue'
import deviceAPI from '@/api/device'
import { getAllBridges } from '@/api/bridge'
import { getWeatherData, getAlertStats } from '@/api/visualization'
import { getBridgeDailyStatistics } from '@/api/account-statistics'
import { getBridgeCrossingRecords } from '@/api/comprehensive-query'
import { getAlertRecordList } from '@/api/alert-record'
import {
  getWaterLevelRealTimeData,
  getVisibilityRealTimeData,
  getAnemometerRealTimeData,
  getFlowmeterRealTimeData
} from '@/api/device-realtime'

export default {
  name: 'ShipMonitor',
  components: {
    ShipMapView
  },
  computed: {
    titleBridgeName() {
      const bridgeName = this.currentBridge && this.currentBridge.name ? this.currentBridge.name : ''
      if (!bridgeName) return 'xx桥'
      return bridgeName.includes('桥') ? bridgeName : `${bridgeName}桥`
    },
    displayDeviceList() {
      return Array.isArray(this.deviceList) ? this.deviceList : []
    },
    displayPlanList() {
      const planList = Array.isArray(this.crossingList) ? this.crossingList.slice(0, 6) : []
      if (planList.length <= 1) return planList
      return planList.concat(planList)
    },
    displayWarningList() {
      const warningList = Array.isArray(this.alertList) ? this.alertList.slice(0, 8) : []
      if (warningList.length <= 1) return warningList
      return warningList.concat(warningList)
    },
    onlineRatePercent() {
      const value = Number(this.deviceOnlineStats.onlineRate || 0)
      if (!Number.isFinite(value)) return 0
      if (value < 0) return 0
      if (value > 100) return 100
      return Number(value.toFixed(1))
    },
    onlineRateProgressColor() {
      return '#c7d2e5'
    },
    /** 环形图线宽随直径比例缩放 */
    onlineRateCircleStrokeWidth() {
      return Math.max(6, Math.round(this.onlineRateProgressSize * (14 / 188)))
    },
    /** 与环形图等大的容器，保证居中且占满剩余高度内的可用边长 */
    onlineRateChartWrapStyle() {
      const s = this.onlineRateProgressSize
      return {
        position: 'relative',
        width: `${s}px`,
        height: `${s}px`,
        flexShrink: 0
      }
    },
    onlineRateCenterWrapStyle() {
      const s = this.onlineRateProgressSize
      return {
        width: `${s}px`,
        height: `${s}px`
      }
    },
    onlineRateCenterValueStyle() {
      const s = this.onlineRateProgressSize
      return {
        fontSize: `${Math.max(15, Math.round(s * 0.165))}px`
      }
    },
    onlineRateCenterLabelStyle() {
      const s = this.onlineRateProgressSize
      return {
        fontSize: `${Math.max(10, Math.round(s * 0.065))}px`,
        marginTop: `${Math.max(4, Math.round(s * 0.028))}px`
      }
    },
    dashboardLoading() {
      return this.loadingState.dashboard || this.loadingState.crossing || this.loadingState.alert
    },
    hydrometeorologicalList() {
      return [
        {
          type: 'water',
          icon: 'el-icon-ship',
          label: '水位监测',
          primaryValue: `净空高 ${this.hydroData.clearance ?? '--'}米`,
          secondaryValue: '',
          statusClass: this.getHydroStatusClass('water')
        },
        {
          type: 'visibility',
          icon: 'el-icon-view',
          label: '能见度监测',
          primaryValue: `能见度 ${this.hydroData.visibility ?? '--'}米`,
          secondaryValue: '',
          statusClass: this.getHydroStatusClass('visibility')
        },
        {
          type: 'wind',
          icon: 'el-icon-wind-power',
          label: '风速风向监测',
          primaryValue: `风速 ${this.hydroData.windSpeed ?? '--'}米/秒`,
          secondaryValue: `风向 ${this.hydroData.windDirection ?? '--'}°`,
          statusClass: this.getHydroStatusClass('wind')
        },
        {
          type: 'flowmeter',
          icon: 'el-icon-refresh',
          label: '流速监测',
          primaryValue: `流速 ${this.hydroData.flowVelocity ?? '--'}米/秒`,
          secondaryValue: '',
          statusClass: this.getHydroStatusClass('flowmeter')
        }
      ]
    }
  },
  data() {
    return {
      lastRoutePath: null,
      nowTime: '',
      currentBridge: {
        id: null,
        name: '',
        latitude: null,
        longitude: null
      },
      headerWeather: {
        waterLevel: '--',
        visibility: '--',
        windSpeed: '--',
        windDirection: '--'
      },
      dashboard: {
        todayNavigationCount: 0,
        todayAlertCount: 0,
        todayDeviationCount: 0,
        todayOverheightCount: 0,
        todayOverspeedCount: 0,
        todayDetentionCount: 0
      },
      crossingList: [],
      alertList: [],
      alertTotalCount: 0,
      deviceList: [],
      deviceOnlineStats: {
        total: 0,
        online: 0,
        offline: 0,
        onlineRate: '0.0'
      },
      hydroData: {
        waterLevel: null,
        clearance: null,
        visibility: null,
        windSpeed: null,
        windDirection: null,
        flowVelocity: null,
        hasWaterLevelDevice: false,
        hasVisibilityDevice: false,
        hasAnemometerDevice: false,
        hasFlowmeterDevice: false,
        waterLevelError: false,
        visibilityError: false,
        anemometerError: false,
        flowmeterError: false
      },
      autoScrollTimers: {},
      autoScrollProgress: {},
      scrollHoverState: {
        plan: false,
        duty: false
      },
      loadingState: {
        headerWeather: false,
        dashboard: false,
        hydrometeorological: false,
        device: false,
        crossing: false,
        alert: false
      },
      timeTimer: null,
      // 仪表盘静默轮询：每分钟整次刷新大盘，单次刷新内接口串行发起
      dashboardPollTimer: null,
      dashboardPollInFlight: false,
      // 设备在线率环形图直径（由卡片剩余区域测量得出）
      onlineRateProgressSize: 120,
      onlineRateResizeObserver: null
    }
  },
  async created() {
    this.$watch(
      () => this.$route,
      this.checkRoute,
      { immediate: true }
    )

    this.$root.$on('menu-clicked', this.handleMenuClick)
    this.$root.$on('menu-modal-trigger', this.handleModalTrigger)

    this.updateNowTime()
    this.timeTimer = setInterval(this.updateNowTime, 1000)

    await this.initializeBridge()
    await this.loadDashboardData()
    this.startRefreshTimers()
  },
  mounted() {
    this.$nextTick(() => {
      this.setupAutoScroll()
      this.setupOnlineRateChartResize()
    })
  },
  beforeDestroy() {
    this.$root.$off('menu-clicked', this.handleMenuClick)
    this.$root.$off('menu-modal-trigger', this.handleModalTrigger)
    if (this.timeTimer) {
      clearInterval(this.timeTimer)
      this.timeTimer = null
    }
    this.clearRefreshTimers()
    this.clearAutoScrollTimers()
    this.teardownOnlineRateChartResize()
  },
  methods: {
    /**
     * 根据「设备在线率」卡片内图表区（扣除顶部统计后的剩余区域）自适应环形图直径
     */
    updateOnlineRateChartSize() {
      this.$nextTick(() => {
        const el = this.$refs.onlineRateMain
        if (!el || typeof el.getBoundingClientRect !== 'function') return
        const rect = el.getBoundingClientRect()
        const w = Math.floor(rect.width)
        const h = Math.floor(rect.height)
        const side = Math.min(w, h)
        // 环形图最大宽高 200px
        const next = Math.max(88, Math.min(200, side - 4))
        if (this.onlineRateProgressSize !== next) {
          this.onlineRateProgressSize = next
        }
      })
    },
    setupOnlineRateChartResize() {
      this.updateOnlineRateChartSize()
      const el = this.$refs.onlineRateMain
      if (!el || typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', this.updateOnlineRateChartSize)
        return
      }
      this.onlineRateResizeObserver = new ResizeObserver(() => {
        this.updateOnlineRateChartSize()
      })
      this.onlineRateResizeObserver.observe(el)
    },
    teardownOnlineRateChartResize() {
      if (this.onlineRateResizeObserver) {
        this.onlineRateResizeObserver.disconnect()
        this.onlineRateResizeObserver = null
      }
      window.removeEventListener('resize', this.updateOnlineRateChartSize)
    },
    handleScrollMouseEnter(key) {
      this.scrollHoverState[key] = true
    },
    handleScrollMouseLeave(key) {
      this.scrollHoverState[key] = false
    },
    clearAutoScrollTimers() {
      Object.keys(this.autoScrollTimers).forEach(key => {
        clearInterval(this.autoScrollTimers[key])
      })
      this.autoScrollTimers = {}
      this.autoScrollProgress = {}
    },
    setupAutoScroll() {
      const resetToTop = () => {
        ;['planScroll', 'dutyScroll'].forEach(refKey => {
          const el = this.$refs[refKey]
          if (el) el.scrollTop = 0
        })
      }
      resetToTop()
      requestAnimationFrame(() => {
        resetToTop()
      })
      this.autoScrollProgress = {}
      this.startAutoScroll('plan', this.$refs.planScroll, 'tbody tr')
      this.startAutoScroll('duty', this.$refs.dutyScroll, 'tbody tr')
    },
    startAutoScroll(key, container, itemSelector) {
      if (this.autoScrollTimers[key]) {
        clearInterval(this.autoScrollTimers[key])
        this.autoScrollTimers[key] = null
      }
      if (!container) return
      if (container.scrollHeight <= container.clientHeight) return
      const firstItem = container.querySelector(itemSelector)
      if (!firstItem) return
      const itemHeight = firstItem.getBoundingClientRect().height
      if (!itemHeight) return
      const getSourceCount = () => {
        if (key === 'plan') return this.crossingList.slice(0, 6).length
        if (key === 'duty') return this.alertList.slice(0, 8).length
        // 其他场景默认按可视行估算
        return Math.max(1, Math.floor(container.scrollHeight / itemHeight))
      }
      const sourceCount = getSourceCount()
      if (sourceCount <= 1) return
      const itemNodes = container.querySelectorAll(itemSelector)
      if (!itemNodes || itemNodes.length <= sourceCount) return
      // 使用小数精度位移计算，避免 offsetTop 整数取整导致回环点抖动
      const containerRect = container.getBoundingClientRect()
      const firstStartOffset = itemNodes[0].getBoundingClientRect().top - containerRect.top + container.scrollTop
      const secondStartOffset = itemNodes[sourceCount].getBoundingClientRect().top - containerRect.top + container.scrollTop
      const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight)
      const loopEndOffset = Math.min(secondStartOffset, maxScrollTop)
      const loopHeight = loopEndOffset - firstStartOffset
      if (!loopHeight || loopHeight <= 0) return
      container.scrollTop = 0
      this.autoScrollProgress[key] = 0
      const pxPerMs = itemHeight / 1500
      let baseTop = 0
      let baseTs = performance.now()
      let pausedByHover = false

      this.autoScrollTimers[key] = setInterval(() => {
        if (this.scrollHoverState[key]) {
          pausedByHover = true
          return
        }
        if (pausedByHover) {
          baseTop = container.scrollTop
          baseTs = performance.now()
          pausedByHover = false
        }
        if (container.scrollHeight <= container.clientHeight) return
        const currentTop = container.scrollTop
        const lastAutoTop = Number(this.autoScrollProgress[key] || 0)
        if (Math.abs(currentTop - lastAutoTop) > 2) {
          baseTop = currentTop
          baseTs = performance.now()
          this.autoScrollProgress[key] = currentTop
        }
        const elapsed = performance.now() - baseTs
        const normalizedBase = ((baseTop - firstStartOffset) % loopHeight + loopHeight) % loopHeight
        const nextRelative = (normalizedBase + elapsed * pxPerMs) % loopHeight
        const nextTop = firstStartOffset + nextRelative
        this.autoScrollProgress[key] = nextTop
        container.scrollTop = nextTop
      }, 16)
    },
    refreshAutoScroll() {
      this.$nextTick(() => {
        this.setupAutoScroll()
      })
    },
    getHydroStatusClass(type) {
      if (type === 'water') {
        if (!this.hydroData.hasWaterLevelDevice) return 'status-disabled'
        return this.hydroData.waterLevelError ? 'status-error' : 'status-normal'
      }
      if (type === 'visibility') {
        if (!this.hydroData.hasVisibilityDevice) return 'status-disabled'
        return this.hydroData.visibilityError ? 'status-error' : 'status-normal'
      }
      if (type === 'wind') {
        if (!this.hydroData.hasAnemometerDevice) return 'status-disabled'
        return this.hydroData.anemometerError ? 'status-error' : 'status-normal'
      }
      if (type === 'flowmeter') {
        if (!this.hydroData.hasFlowmeterDevice) return 'status-disabled'
        return this.hydroData.flowmeterError ? 'status-error' : 'status-normal'
      }
      return 'status-disabled'
    },
    updateNowTime() {
      this.nowTime = this.formatDateTime(new Date())
    },
    getTodayRange() {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      const h = String(now.getHours()).padStart(2, '0')
      const i = String(now.getMinutes()).padStart(2, '0')
      const s = String(now.getSeconds()).padStart(2, '0')
      return {
        startTime: `${y}-${m}-${d} 00:00:00`,
        endTime: `${y}-${m}-${d} ${h}:${i}:${s}`
      }
    },
    formatDateTime(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const i = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${i}:${s}`
    },
    formatDisplayTime(timeValue) {
      if (!timeValue) return '-'
      if ((typeof timeValue === 'string' && timeValue.includes(',')) || Array.isArray(timeValue)) {
        const rawParts = Array.isArray(timeValue)
          ? timeValue
          : timeValue.replace(/^\[|\]$/g, '').split(',')
        const parts = rawParts.map(item => Number(String(item).trim()))
        if (parts.length >= 6 && parts.slice(0, 6).every(num => Number.isFinite(num))) {
          const [year, month, day, hour, minute, second] = parts
          const y = String(year).padStart(4, '0')
          const m = String(month).padStart(2, '0')
          const d = String(day).padStart(2, '0')
          const h = String(hour).padStart(2, '0')
          const i = String(minute).padStart(2, '0')
          const s = String(second).padStart(2, '0')
          return `${y}-${m}-${d} ${h}:${i}:${s}`
        }
      }
      const date = new Date(timeValue)
      if (Number.isNaN(date.getTime())) {
        const normalized = String(timeValue)
          .replace('T', ' ')
          .replace(/\.\d+Z?$/, '')
          .replace(/(\d{4}-\d{2}-\d{2})\s*(\d{2}:\d{2}:\d{2})/, '$1 $2')
        return normalized
      }
      return this.formatDateTime(date)
    },
    formatWeatherValue(value, digit = 1) {
      if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
        return '--'
      }
      return Number(value).toFixed(digit)
    },
    calculateWindDirection(degrees) {
      if (degrees === null || degrees === undefined || Number.isNaN(Number(degrees))) {
        return '--'
      }
      const labels = ['北', '东北', '东', '东南', '南', '西南', '西', '西北']
      const normalized = ((Number(degrees) % 360) + 360) % 360
      const index = Math.round(normalized / 45) % 8
      return labels[index]
    },
    formatHydroNumber(value, digit = 1) {
      if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
        return '--'
      }
      return Number(value).toFixed(digit)
    },
    /**
     * 水位实时值解析（与 HydrometeorologicalIndicator.extractValue 一致：仅数值型保留两位小数）
     */
    extractHydroWaterLevelValue(realTimeValue) {
      if (!realTimeValue) return null
      if (typeof realTimeValue === 'number') {
        return parseFloat(realTimeValue.toFixed(2))
      }
      return null
    },
    /**
     * 净空高换算（与 HydrometeorologicalIndicator.extractClearance 一致：emptyHeight 与 bridgeClearanceHeight 均有则相减）
     */
    extractHydroClearance(extraProperties) {
      if (!extraProperties) return null
      let clearance = null
      if (extraProperties.emptyHeight !== undefined && extraProperties.emptyHeight !== null) {
        const emptyHeight = parseFloat(extraProperties.emptyHeight)
        clearance = Number.isNaN(emptyHeight) ? null : parseFloat(emptyHeight.toFixed(2))
      }
      let bridgeClearanceHeight = null
      if (extraProperties.bridgeClearanceHeight !== undefined && extraProperties.bridgeClearanceHeight !== null) {
        const bridgeClearanceHeightRaw = parseFloat(extraProperties.bridgeClearanceHeight)
        bridgeClearanceHeight = Number.isNaN(bridgeClearanceHeightRaw)
          ? null
          : parseFloat(bridgeClearanceHeightRaw.toFixed(2))
      }
      if (clearance !== null && bridgeClearanceHeight !== null) {
        return clearance - bridgeClearanceHeight
      }
      return null
    },
    /**
     * 能见度（与 HydrometeorologicalIndicator.fetchVisibilityData 一致：realTimeValue 为数字时 toFixed(1)）
     */
    extractHydroVisibilityValue(realTimeValue) {
      if (realTimeValue === null || realTimeValue === undefined) return null
      if (typeof realTimeValue === 'number') {
        return parseFloat(realTimeValue.toFixed(1))
      }
      return null
    },
    /** 风速（与 extractWindSpeed 一致） */
    extractHydroWindSpeed(realTimeValue) {
      if (!realTimeValue) return null
      if (typeof realTimeValue === 'number') {
        return parseFloat(realTimeValue.toFixed(1))
      }
      if (typeof realTimeValue === 'object') {
        const possibleFields = ['windSpeed', 'speed', 'value', 'data', 'velocity']
        for (const field of possibleFields) {
          if (realTimeValue[field] !== undefined && realTimeValue[field] !== null) {
            const value = parseFloat(realTimeValue[field])
            return isNaN(value) ? null : parseFloat(value.toFixed(1))
          }
        }
      }
      return null
    },
    /** 风向角度（与 extractWindDirection 一致） */
    extractHydroWindDirection(realTimeValue) {
      if (!realTimeValue) return null
      if (typeof realTimeValue === 'number') {
        return parseFloat(realTimeValue.toFixed(0))
      }
      if (typeof realTimeValue === 'object') {
        const possibleFields = ['windDirection', 'direction', 'angle', 'bearing', 'heading']
        for (const field of possibleFields) {
          if (realTimeValue[field] !== undefined && realTimeValue[field] !== null) {
            const value = parseFloat(realTimeValue[field])
            return isNaN(value) ? null : parseFloat(value.toFixed(0))
          }
        }
      }
      return null
    },
    /** 流速（与 extractFlowVelocity 一致） */
    extractHydroFlowVelocity(realTimeValue) {
      if (!realTimeValue) return null
      if (typeof realTimeValue === 'number') {
        return parseFloat(realTimeValue.toFixed(2))
      }
      if (typeof realTimeValue === 'object') {
        const possibleFields = ['flowVelocity', 'velocity', 'speed', 'value', 'data']
        for (const field of possibleFields) {
          if (realTimeValue[field] !== undefined && realTimeValue[field] !== null) {
            const value = parseFloat(realTimeValue[field])
            return isNaN(value) ? null : parseFloat(value.toFixed(2))
          }
        }
      }
      return null
    },
    /**
     * 水文气象数据
     * @param {boolean} silent 是否静默（不展示整卡 loading）；静默刷新时四个实时接口串行请求，首屏非静默时仍并行请求
     */
    async loadHydrometeorologicalData(silent = false) {
      if (!this.currentBridge.id) return
      const bridgeId = this.currentBridge.id
      if (!silent) this.loadingState.hydrometeorological = true
      try {
        let waterRes
        let visibilityRes
        let windRes
        let flowRes
        if (!silent) {
          ;[waterRes, visibilityRes, windRes, flowRes] = await Promise.all([
            getWaterLevelRealTimeData(bridgeId).catch(() => null),
            getVisibilityRealTimeData(bridgeId).catch(() => null),
            getAnemometerRealTimeData(bridgeId).catch(() => null),
            getFlowmeterRealTimeData(bridgeId).catch(() => null)
          ])
        } else {
          waterRes = await getWaterLevelRealTimeData(bridgeId).catch(() => null)
          visibilityRes = await getVisibilityRealTimeData(bridgeId).catch(() => null)
          windRes = await getAnemometerRealTimeData(bridgeId).catch(() => null)
          flowRes = await getFlowmeterRealTimeData(bridgeId).catch(() => null)
        }

        const nextData = {
          waterLevel: null,
          clearance: null,
          visibility: null,
          windSpeed: null,
          windDirection: null,
          flowVelocity: null,
          hasWaterLevelDevice: false,
          hasVisibilityDevice: false,
          hasAnemometerDevice: false,
          hasFlowmeterDevice: false,
          waterLevelError: false,
          visibilityError: false,
          anemometerError: false,
          flowmeterError: false
        }

        if (waterRes && waterRes.code === 200 && Array.isArray(waterRes.data) && waterRes.data.length) {
          nextData.hasWaterLevelDevice = true
          const latest = waterRes.data[0] || {}
          const wlNum = this.extractHydroWaterLevelValue(latest.realTimeValue)
          nextData.waterLevel = wlNum === null ? '--' : this.formatHydroNumber(wlNum, 2)
          const clearanceNum = this.extractHydroClearance(latest.extraProperties)
          nextData.clearance = clearanceNum === null ? '--' : this.formatHydroNumber(clearanceNum, 2)
        } else if (waterRes && waterRes.code && waterRes.code !== 200) {
          nextData.waterLevelError = true
        }

        if (visibilityRes && visibilityRes.code === 200 && Array.isArray(visibilityRes.data) && visibilityRes.data.length) {
          nextData.hasVisibilityDevice = true
          const latest = visibilityRes.data[0] || {}
          const visNum = this.extractHydroVisibilityValue(latest.realTimeValue)
          nextData.visibility = visNum === null ? '--' : this.formatHydroNumber(visNum, 1)
        } else if (visibilityRes && visibilityRes.code && visibilityRes.code !== 200) {
          nextData.visibilityError = true
        }

        if (windRes && windRes.code === 200 && Array.isArray(windRes.data) && windRes.data.length) {
          nextData.hasAnemometerDevice = true
          const latest = windRes.data[0] || {}
          const wsNum = this.extractHydroWindSpeed(latest.realTimeValue)
          const wdNum = this.extractHydroWindDirection(latest.realTimeValue)
          nextData.windSpeed = wsNum === null ? '--' : this.formatHydroNumber(wsNum, 1)
          nextData.windDirection = wdNum === null ? '--' : this.formatHydroNumber(wdNum, 0)
        } else if (windRes && windRes.code && windRes.code !== 200) {
          nextData.anemometerError = true
        }

        if (flowRes && flowRes.code === 200 && Array.isArray(flowRes.data) && flowRes.data.length) {
          nextData.hasFlowmeterDevice = true
          const latest = flowRes.data[0] || {}
          const fvNum = this.extractHydroFlowVelocity(latest.realTimeValue)
          nextData.flowVelocity = fvNum === null ? '--' : this.formatHydroNumber(fvNum, 2)
        } else if (flowRes && flowRes.code && flowRes.code !== 200) {
          nextData.flowmeterError = true
        }

        this.hydroData = nextData
      } finally {
        if (!silent) this.loadingState.hydrometeorological = false
      }
    },
    setDefaultHeaderWeather() {
      this.headerWeather = {
        waterLevel: '--',
        visibility: '--',
        windSpeed: '--',
        windDirection: '--'
      }
    },
    async loadHeaderWeatherData(silent = false) {
      if (!this.currentBridge.id) {
        this.setDefaultHeaderWeather()
        return
      }
      if (!silent) this.loadingState.headerWeather = true
      try {
        const response = await getWeatherData(this.currentBridge.id)
        if (!(response && response.code === 200 && response.data)) {
          this.setDefaultHeaderWeather()
          return
        }
        const data = response.data

        const waterRaw = data.waterLevelData && data.waterLevelData[0] ? data.waterLevelData[0].realTimeValue : null
        const visibilityRaw = data.visibilityData && data.visibilityData[0] ? data.visibilityData[0].realTimeValue : null
        const windRaw = data.anemometerData && data.anemometerData[0] ? data.anemometerData[0].realTimeValue : null

        const visibilityValue = this.formatWeatherValue(visibilityRaw)
        this.headerWeather = {
          waterLevel: this.formatWeatherValue(waterRaw),
          visibility: visibilityValue === '--' ? '--' : (Number(visibilityValue) / 1000).toFixed(1),
          windSpeed: this.formatWeatherValue(windRaw && (windRaw.windSpeed ?? windRaw.speed)),
          windDirection: this.calculateWindDirection(windRaw && (windRaw.windDirection ?? windRaw.direction))
        }
      } catch (error) {
        console.error('加载标题天气数据失败:', error)
        this.setDefaultHeaderWeather()
      } finally {
        console.log('标题天气数据加载完成', 'mll---')
        if (!silent) this.loadingState.headerWeather = false
      }
    },
    resolveBridgeId(bridge) {
      if (!bridge) return null
      return bridge.id || bridge.bridgeId || bridge.bridgeID || null
    },
    normalizeBridge(bridge) {
      return {
        id: this.resolveBridgeId(bridge),
        name: bridge.name || bridge.bridgeName || '',
        latitude: bridge.latitude,
        longitude: bridge.longitude
      }
    },
    loadBridgeFromStorage() {
      try {
        const selectedBridge = localStorage.getItem('selectedBridge')
        if (!selectedBridge) return false
        const bridgeInfo = JSON.parse(selectedBridge)
        const normalized = this.normalizeBridge(bridgeInfo)
        if (!normalized.id) return false
        this.currentBridge = normalized
        return true
      } catch (error) {
        console.error('读取本地桥梁信息失败:', error)
        return false
      } finally {
        console.log('读取本地桥梁信息完成', 'mll---')
      }
    },
    async initializeBridge() {
      if (this.loadBridgeFromStorage()) {
        this.$root.$emit('bridge-changed', this.currentBridge)
        return
      }

      try {
        const res = await getAllBridges()
        const bridgeList = res && res.code === 200 && Array.isArray(res.data) ? res.data : []
        if (!bridgeList.length) {
          // 与 DataVisualization 保持一致：使用默认桥梁 ID，避免接口完全不请求
          this.currentBridge = {
            id: '227',
            name: '未知桥梁',
            latitude: null,
            longitude: null
          }
          this.$root.$emit('bridge-changed', this.currentBridge)
          return
        }

        let defaultBridge = bridgeList.find(item => {
          const name = item.bridgeName || item.name || ''
          return name.includes('封开西江大桥')
        })
        if (!defaultBridge) {
          defaultBridge = bridgeList[0]
        }

        const normalized = this.normalizeBridge(defaultBridge)
        if (!normalized.id) {
          this.currentBridge = {
            id: '227',
            name: normalized.name || '未知桥梁',
            latitude: normalized.latitude,
            longitude: normalized.longitude
          }
          this.$root.$emit('bridge-changed', this.currentBridge)
          return
        }
        this.currentBridge = normalized
        localStorage.setItem('selectedBridge', JSON.stringify(normalized))
        this.$root.$emit('bridge-changed', normalized)
      } catch (error) {
        console.error('初始化默认桥梁失败:', error)
        this.currentBridge = {
          id: '227',
          name: '未知桥梁',
          latitude: null,
          longitude: null
        }
        this.$root.$emit('bridge-changed', this.currentBridge)
      } finally {
        console.log('初始化默认桥梁完成', 'mll---')
      }
    },
    /**
     * 按批执行异步任务，每批最多 concurrent 个并行（首屏用 thunk 避免一次性发出全部请求）
     */
    async runTaskThunksInBatches(thunks, concurrent = 2) {
      for (let i = 0; i < thunks.length; i += concurrent) {
        await Promise.all(thunks.slice(i, i + concurrent).map(fn => fn()))
      }
    },
    /**
     * 加载仪表盘各模块；首屏每批最多 2 个模块并行；静默轮询时各模块及水文子接口均串行
     */
    async loadDashboardData(silent = false) {
      if (!this.currentBridge.id) return

      if (silent) {
        await this.loadOverviewData(silent)
        await this.loadCrossingData(silent)
        await this.loadAlertData(silent)
        await this.loadDevicePanelData(silent)
        await this.loadHeaderWeatherData(silent)
        await this.loadHydrometeorologicalData(silent)
        return
      }

      const thunks = [
        () => this.loadOverviewData(silent),
        () => this.loadCrossingData(silent),
        () => this.loadAlertData(silent),
        () => this.loadDevicePanelData(silent),
        () => this.loadHeaderWeatherData(silent),
        () => this.loadHydrometeorologicalData(silent)
      ]
      await this.runTaskThunksInBatches(thunks, 2)
    },
    async loadOverviewData(silent = false) {
      if (!silent) this.loadingState.dashboard = true
      try {
        const dailyRes = await getBridgeDailyStatistics(this.currentBridge.id).catch(() => null)
        if (dailyRes && dailyRes.code === 200 && dailyRes.data) {
          const data = dailyRes.data
          // 与 DataVisualization 一致：通航总数取日统计接口 todayNavigationCount
          this.dashboard.todayNavigationCount = data.todayNavigationCount || 0
          this.dashboard.todayDeviationCount = data.todayDeviationCount || 0
          this.dashboard.todayOverheightCount = data.todayOverheightCount || 0
          this.dashboard.todayOverspeedCount = data.todayOverspeedCount || 0
          this.dashboard.todayDetentionCount = data.todayDetentionCount || 0
        }
        const alertStatsRes = await getAlertStats(this.currentBridge.id).catch(() => null)
        if (alertStatsRes && alertStatsRes.code === 200 && alertStatsRes.data) {
          // 与 DataVisualization 一致：预警总数取 getAlertStats 的 todayCount
          this.dashboard.todayAlertCount = alertStatsRes.data.todayCount || 0
        }
      } finally {
        console.log('船舶概览（今日）数据加载完成', 'mll---')
        if (!silent) this.loadingState.dashboard = false
      }
    },
    async loadCrossingData(silent = false) {
      const { startTime, endTime } = this.getTodayRange()
      if (!silent) this.loadingState.crossing = true
      try {
        const crossingRes = await getBridgeCrossingRecords({
          bridgeId: this.currentBridge.id,
          startTime,
          endTime,
          pageNum: 1,
          pageSize: 20
        }).catch(() => null)
        if (crossingRes && crossingRes.code === 200 && crossingRes.data && Array.isArray(crossingRes.data.list)) {
          this.crossingList = crossingRes.data.list.map(item => ({
            id: item.id,
            vesselName: item.vesselName || '-',
            vesselMmsi: item.vesselMmsi || '-',
            bridgeName: item.bridgeName || '-',
            approachDirection: item.approachDirection,
            eventTypeDescription: item.eventTypeDescription || item.eventType || '-',
            approachDirectionName: item.approachDirectionName || '-',
            speed: item.speed !== null && item.speed !== undefined ? `${Number(item.speed).toFixed(1)}kn` : '--',
            speedDisplay: item.speed !== null && item.speed !== undefined ? Number(item.speed).toFixed(1) : '-',
            eventTime: item.eventTime || '-'
          }))
        } else {
          this.crossingList = []
        }
        this.refreshAutoScroll()
      } finally {
        console.log('桥梁通航记录数据加载完成', 'mll---')
        if (!silent) this.loadingState.crossing = false
      }
    },
    async loadAlertData(silent = false) {
      const { startTime, endTime } = this.getTodayRange()
      if (!silent) this.loadingState.alert = true
      try {
        const alertRes = await getAlertRecordList({
          bridgeId: this.currentBridge.id,
          startTimeStr: startTime,
          endTimeStr: endTime,
          pageNum: 1,
          pageSize: 20
        }).catch(() => null)
        if (alertRes && alertRes.code === 200 && alertRes.data && Array.isArray(alertRes.data.list)) {
          this.alertTotalCount = Number(alertRes.data.total || 0)
          this.alertList = alertRes.data.list.map(item => ({
            id: item.id,
            sourceId: item.sourceId || '-',
            alertLevel: item.alertLevel,
            alertLevelText: this.getAlertLevelText(item.alertLevel),
            ruleType: item.ruleType || '预警',
            status: item.status,
            statusText: this.getAlertStatusText(item.status),
            alertMessage: item.alertMessage || '-',
            alertTime: item.alertTime || '-'
          }))
        } else {
          this.alertTotalCount = 0
          this.alertList = []
        }
        this.refreshAutoScroll()
      } finally {
        console.log('预警数据加载完成', 'mll---')
        if (!silent) this.loadingState.alert = false
      }
    },
    async loadDevicePanelData(silent = false) {
      if (!silent) this.loadingState.device = true
      try {
        const deviceRes = await this.loadDeviceData().catch(() => [])
        this.deviceList = Array.isArray(deviceRes && deviceRes.rows) ? deviceRes.rows : []
        this.deviceOnlineStats = deviceRes && deviceRes.stats
          ? deviceRes.stats
          : { total: 0, online: 0, offline: 0, onlineRate: '0.0' }
      } finally {
        console.log('设备数据加载完成', 'mll---')
        if (!silent) this.loadingState.device = false
      }
    },
    async loadDeviceData() {
      if (!this.currentBridge.id) {
        return {
          rows: [],
          stats: { total: 0, online: 0, offline: 0, onlineRate: '0.0' }
        }
      }

      const response = await deviceAPI.getDevicesByBridge(this.currentBridge.id)
      const sourceList = response && response.code === 200 && Array.isArray(response.data) ? response.data : []
      const rows = sourceList.map(item => ({
        name: item.name || '-',
        type: item.device_type || '-',
        location: item.location || '-',
        status: item.status || 'offline',
        statusText: item.status === 'online' ? '在线' : '离线',
        statusType: item.status === 'online' ? 'success' : 'info'
      }))

      const total = sourceList.length
      let online = 0
      sourceList.forEach(device => {
        if (device.status === 'online') {
          online++
        }
      })
      const offline = Math.max(0, total - online)
      const onlineRate = total > 0 ? ((online / total) * 100).toFixed(1) : '0.0'

      return {
        rows: rows.slice(0, 20),
        stats: {
          total,
          online,
          offline,
          onlineRate
        }
      }
    },
    /**
     * 启动仪表盘静默轮询：每 60s 执行一整次大盘刷新（盘内请求串行，见 loadDashboardData(true)）
     */
    startRefreshTimers() {
      this.clearRefreshTimers()
      this.dashboardPollTimer = setInterval(() => {
        // 上一轮静默刷新未完成则跳过本分钟 tick，避免请求堆叠
        if (this.dashboardPollInFlight) return
        this.runDashboardSilentRefresh()
      }, 60000)
    },
    /**
     * 执行一次完整静默刷新；若上一轮尚未结束则跳过（定时器入口与函数内双重判断），避免请求堆叠
     */
    async runDashboardSilentRefresh() {
      if (!this.currentBridge.id || this.dashboardPollInFlight) return
      this.dashboardPollInFlight = true
      try {
        await this.loadDashboardData(true)
      } catch (e) {
        console.error('仪表盘静默刷新失败:', e)
      } finally {
        console.log('仪表盘静默刷新完成', new Date(), 'mll---')
        this.dashboardPollInFlight = false
      }
    },
    clearRefreshTimers() {
      if (this.dashboardPollTimer) {
        clearInterval(this.dashboardPollTimer)
        this.dashboardPollTimer = null
      }
    },
    getAlertLevelText(level) {
      const numLevel = Number(level)
      if (numLevel === 1) return '低'
      if (numLevel === 2) return '信息'
      if (numLevel === 3) return '警告'
      if (numLevel === 4) return '危险'
      if (numLevel === 5) return '严重'
      return '-'
    },
    getAlertStatusText(status) {
      const numStatus = Number(status)
      if (numStatus === 0) return '未处理'
      if (numStatus === 1) return '已处理'
      if (numStatus === 2) return '已忽略'
      return '-'
    },
    getAlertStatusType(status) {
      const numStatus = Number(status)
      if (numStatus === 0) return 'warning'
      if (numStatus === 1) return 'success'
      if (numStatus === 2) return 'info'
      return 'info'
    },
    getDirectionTagType(direction) {
      return Number(direction) === 0 ? 'success' : 'primary'
    },
    getDirectionText(direction, directionName) {
      if (directionName && directionName !== '-') return directionName
      const directionValue = Number(direction)
      if (directionValue === 0) return '下行'
      if (directionValue === 1) return '上行'
      return '-'
    },
    showPlanShipTrack(record) {
      if (!record || !record.vesselMmsi || record.vesselMmsi === '-') return
      const shipMapView = this.$refs.shipMapView
      if (!shipMapView || typeof shipMapView.showShipTrack !== 'function') return
      shipMapView.showShipTrack(String(record.vesselMmsi))
    },
    /**
     * 处理菜单点击事件，直接显示对应弹窗
     */
    handleMenuClick(eventData) {
      console.log('菜单点击事件:', eventData)

      // 延迟执行，确保ShipMapView组件已完全加载
      this.$nextTick(() => {
        const shipMapView = this.$refs.shipMapView
        if (!shipMapView) {
          console.warn('ShipMapView组件未找到')
          return
        }

        // 根据菜单ID显示对应的弹窗
        switch (eventData.menuId) {
          case 'bridgeRecord':
            // 桥梁通航记录
            shipMapView.trafficPanelVisible = true
            shipMapView.bringPanelToFront('traffic')
            break

          case 'bridgeWarningAll':
            // 桥梁预警信息
            shipMapView.alertDetailVisible = true
            shipMapView.alertActiveTab = 'records'
            shipMapView.bringPanelToFront('alert')
            break

          case 'statisticsYaw':
            // 桥梁偏航统计
            shipMapView.deviationStatisticsVisible = true
            shipMapView.statisticsActiveTab = 'deviation'
            shipMapView.bringPanelToFront('statistics')
            break

          case 'statisticsBridgeReportRecord':
            // 流量统计
            shipMapView.deviationStatisticsVisible = true
            shipMapView.statisticsActiveTab = 'traffic'
            shipMapView.bringPanelToFront('statistics')
            break

          case 'statisticsBridge':
            // 桥梁预警统计
            shipMapView.deviationStatisticsVisible = true
            shipMapView.statisticsActiveTab = 'alert'
            shipMapView.bringPanelToFront('statistics')
            break

          case 'statisticsSuperelevation':
            // 桥梁超高统计 - 使用同一个统计面板
            shipMapView.deviationStatisticsVisible = true
            shipMapView.statisticsActiveTab = 'superelevation'
            shipMapView.bringPanelToFront('statistics')
            break

          case 'statisticsOverspeed':
            // 桥梁超速统计 - 使用同一个统计面板
            shipMapView.deviationStatisticsVisible = true
            shipMapView.statisticsActiveTab = 'overspeed'
            shipMapView.bringPanelToFront('statistics')
            break

          case 'statisticsBridgeForbidRecord':
            // 桥梁禁航统计 - 使用同一个统计面板
            shipMapView.deviationStatisticsVisible = true
            shipMapView.statisticsActiveTab = 'forbid'
            shipMapView.bringPanelToFront('statistics')
            break

          case 'statisticsVhf':
            // 桥梁VHF统计 - 使用同一个统计面板
            shipMapView.deviationStatisticsVisible = true
            shipMapView.statisticsActiveTab = 'vhf'
            shipMapView.bringPanelToFront('statistics')
            break

          case 'vhfPushAll':
            // VHF呼叫记录
            shipMapView.vhfCallRecordVisible = true
            shipMapView.bringPanelToFront('vhf')
            break

          case 'bridgeRuleAll':
            // 桥梁预警规则
            shipMapView.bridgeAlertRulesVisible = true
            shipMapView.bridgeRulesActiveTab = 'rules'
            shipMapView.bringPanelToFront('bridgeRules')
            break

          default:
            console.log('未处理的菜单项:', eventData.menuId)
        }
      })
    },

    /**
     * 处理弹窗触发事件
     */
    handleModalTrigger(eventData) {
      console.log('【ShipMonitor】弹窗触发事件:', eventData)

      // 延迟执行，确保ShipMapView组件已完全加载
      this.$nextTick(() => {
        const shipMapView = this.$refs.shipMapView
        if (!shipMapView) {
          console.warn('ShipMapView组件未找到')
          return
        }

        // 根据modalTrigger显示对应的弹窗
        switch (eventData.modalTrigger) {
          case 'bridgeTrafficRecord':
            // 桥梁通航记录
            console.log('【ShipMonitor】显示桥梁通航记录面板')
            shipMapView.trafficPanelVisible = true
            shipMapView.bringPanelToFront('traffic')
            break

          case 'bridgeAlerts':
            // 桥梁预警信息
            console.log('【ShipMonitor】显示桥梁预警信息面板')
            shipMapView.alertDetailVisible = true
            shipMapView.alertActiveTab = 'records'
            shipMapView.bringPanelToFront('alert')
            break

          case 'bridgeDeviationStatistics':
            // 统计分析面板
            console.log('【ShipMonitor】显示统计分析面板, 标签页:', eventData.modalTab)
            shipMapView.deviationStatisticsVisible = true
            if (eventData.modalTab) {
              shipMapView.statisticsActiveTab = eventData.modalTab
            }
            shipMapView.bringPanelToFront('statistics')
            break

          default:
            console.log('未处理的弹窗触发器:', eventData.modalTrigger)
        }
      })
    },

    /**
     * 检查当前路由是否需要显示告警面板
     */
    checkRoute(route) {
      // 检查路由是否真正发生了变化
      const currentPath = route.path
      const isRouteChanged = this.lastRoutePath !== currentPath
      this.lastRoutePath = currentPath

      if (route.meta && route.meta.showAlertPanel) {
        // 延迟执行，确保ShipMapView组件已完全加载
        this.$nextTick(() => {
          const shipMapView = this.$refs.shipMapView
          if (shipMapView) {
            // 如果路由发生了变化，或者面板未显示，则显示面板
            if (isRouteChanged || !shipMapView.alertDetailVisible) {
              shipMapView.alertDetailVisible = true
              shipMapView.alertActiveTab = 'records'
            }
          }
        })
      }

      if (route.meta && route.meta.showTrafficPanel) {
        // 延迟执行，确保ShipMapView组件已完全加载
        this.$nextTick(() => {
          console.log('showTrafficPanel@nextTick', route.meta.showTrafficPanel, 'isRouteChanged:', isRouteChanged)

          const shipMapView = this.$refs.shipMapView
          if (shipMapView) {
            // 如果路由发生了变化，或者面板未显示，则显示面板
            if (isRouteChanged || !shipMapView.trafficPanelVisible) {
              shipMapView.trafficPanelVisible = true
            }
          }
        })
      }

      if (route.meta && route.meta.showDeviationStatistics) {
        // 延迟执行，确保ShipMapView组件已完全加载
        this.$nextTick(() => {
          console.log('showDeviationStatistics@nextTick', route.meta.showDeviationStatistics, 'isRouteChanged:', isRouteChanged)

          const shipMapView = this.$refs.shipMapView
          if (shipMapView) {
            // 如果路由发生了变化，或者面板未显示，则显示面板
            if (isRouteChanged || !shipMapView.deviationStatisticsVisible) {
              shipMapView.deviationStatisticsVisible = true
              // 设置活跃标签页
              if (route.meta.statisticsActiveTab) {
                shipMapView.statisticsActiveTab = route.meta.statisticsActiveTab
              }
            }
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.ship-monitor-screen {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  color: #dbe7ff;
  background: radial-gradient(circle at 50% -10%, rgba(30, 58, 138, 0.28) 0%, rgba(20, 37, 88, 0.12) 45%, rgba(0, 0, 0, 0) 80%);
}

.ship-monitor-screen::before {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid rgba(74, 120, 255, 0.35);
  box-shadow: inset 0 0 22px rgba(37, 72, 170, 0.22);
  pointer-events: none;
  z-index: 2;
}

.map-layer {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.map-layer ::v-deep .ship-map-container,
.map-layer ::v-deep .ship-monitor-container,
.map-layer ::v-deep .ship-map-view-container {
  width: 100%;
  height: 100%;
}

.overlay-layer {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  padding: 8px 10px 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  pointer-events: none;
}

.overlay-layer > * {
  pointer-events: auto;
}

.screen-header {
  position: relative;
  height: max-content;
  text-align: center;
  border: 1px solid rgba(88, 132, 255, 0.5);
  background: linear-gradient(180deg, rgba(37, 72, 170, 0.8) 0%, rgba(22, 44, 112, 0.8) 100%);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  border-radius: 10px;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: 16px 18px 14px;
  margin-bottom: 8px;
  box-shadow:
    inset 0 1px 0 rgba(177, 201, 255, 0.55),
    inset 0 -10px 24px rgba(24, 49, 121, 0.5),
    0 0 16px rgba(46, 92, 210, 0.35);
  overflow: hidden;
}

.screen-header {
  position: absolute;
  top: 8px;
  left: 408px;
  right: 408px;
  width: auto;
  transform: none;
  z-index: 3;
}

.screen-header::before {
  content: '';
  position: absolute;
  left: 22%;
  right: 22%;
  top: 6px;
  height: 2px;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(118, 242, 255, 0.95) 50%, rgba(0, 0, 0, 0) 100%);
  filter: drop-shadow(0 0 4px rgba(118, 242, 255, 0.75));
}

.screen-header::after {
  content: '';
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 0;
  height: 1px;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, rgba(97, 223, 255, 0.65) 50%, rgba(0, 0, 0, 0.5) 100%);
}

.header-title {
  font-size: 38px;
  font-weight: 700;
  letter-spacing: 8px;
  line-height: 1;
  color: #a6f6ff;
  text-shadow:
    0 0 8px rgba(101, 232, 255, 0.65),
    0 0 18px rgba(35, 167, 255, 0.55),
    0 0 28px rgba(23, 114, 225, 0.35);
}

.header-subline {
  margin-top: 8px;
  width: min(76%, 980px);
  height: 20px;
  line-height: 20px;
  font-size: 11px;
  color: #95e7ff;
  letter-spacing: 0.5px;
  text-align: center;
  overflow: hidden;
  position: relative;
}

.header-subline::before,
.header-subline::after {
  content: '';
  position: absolute;
  top: 0;
  width: 36px;
  height: 100%;
  z-index: 1;
}

.header-subline::before {
  left: 0;
  background: linear-gradient(90deg, rgba(5, 27, 57, 0.95) 0%, rgba(5, 27, 57, 0) 100%);
}

.header-subline::after {
  right: 0;
  background: linear-gradient(270deg, rgba(5, 27, 57, 0.95) 0%, rgba(5, 27, 57, 0) 100%);
}

.header-subline-track {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: max-content;
  animation: header-news-scroll 12s ease-in-out infinite alternate;
}

.header-item {
  text-shadow: 0 0 6px rgba(58, 197, 255, 0.36);
}

.header-divider {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: rgba(126, 232, 255, 0.72);
  box-shadow: 0 0 5px rgba(126, 232, 255, 0.6);
}

@keyframes header-news-scroll {
  from {
    transform: translateX(6%);
  }
  to {
    transform: translateX(-6%);
  }
}

.header-meta {
  display: flex;
  gap: 16px;
  font-size: 14px;
  color: #8fdfff;
}

.screen-main {
  flex: 1;
  height: 100%;
  min-height: 0;
  display: grid;
  grid-template-columns: 390px 1fr 390px;
  gap: 8px;
  /* 中间区域事件透传给地图，避免阻挡地图缩放/拖拽 */
  pointer-events: none;
}

.side-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* 左右面板保持可交互 */
  pointer-events: auto;
}

.left-panel {
  grid-column: 1;
}

.right-panel {
  grid-column: 3;
}

.panel-card,
.bottom-card {
  border: 1px solid rgba(82, 126, 246, 0.35);
  background: linear-gradient(180deg, rgba(32, 63, 148, 0.76) 0%, rgba(20, 40, 101, 0.8) 100%);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  border-radius: 6px;
  padding: 8px 10px;
  box-sizing: border-box;
  box-shadow:
    inset 0 0 24px rgba(52, 96, 214, 0.2),
    inset 0 1px 0 rgba(160, 188, 255, 0.3),
    0 0 8px rgba(35, 70, 172, 0.28);
  position: relative;
  overflow: hidden;
}

.panel-card::before,
.bottom-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(84, 228, 255, 0.88) 50%, rgba(0, 0, 0, 0) 100%);
  opacity: 0.8;
}

.stats-card {
  min-height: 0;
  flex: 1;
}

.list-card {
  flex: 1;
  min-height: 0;
}

.left-panel .stats-card,
.left-panel .list-card {
  flex: 1;
  min-height: 0;
}

.card-title {
  position: relative;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
  padding: 0 0 6px 10px;
  color: #d9e5ff;
  letter-spacing: 1px;
  border-bottom: 1px solid rgba(100, 136, 248, 0.6);
  text-shadow: 0 0 6px rgba(120, 160, 255, 0.45);
  background: linear-gradient(90deg, rgba(26, 50, 118, 0.78) 0%, rgba(26, 50, 118, 0.36) 70%, rgba(26, 50, 118, 0) 100%);
  border-radius: 4px 4px 0 0;
}

.card-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  width: 3px;
  height: 18px;
  border-radius: 2px;
  background: linear-gradient(180deg, rgba(138, 245, 255, 1) 0%, rgba(39, 157, 255, 0.95) 100%);
  box-shadow: 0 0 8px rgba(79, 229, 255, 0.65);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.stat-item {
  border: 1px solid rgba(95, 132, 245, 0.32);
  background: linear-gradient(180deg, rgba(40, 73, 165, 0.62) 0%, rgba(25, 49, 120, 0.58) 100%);
  border-radius: 4px;
  padding: 6px 7px;
  box-shadow: inset 0 0 14px rgba(52, 92, 208, 0.2);
}

.stat-label {
  font-size: 12px;
  color: #b8ccff;
}

.stat-value {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.15;
  color: #d8fbff;
  text-shadow: 0 0 10px rgba(80, 224, 255, 0.45);
}

.list-body {
  height: calc(100% - 30px);
  overflow: auto;
  padding-right: 2px;
}

.hydro-body {
  height: calc(100% - 30px);
  overflow: auto;
  padding-right: 2px;
}

.hydro-item {
  display: flex;
  align-items: center;
  padding: 10px 8px;
  border-radius: 8px;
  margin-bottom: 6px;
  border: 1px solid rgba(102, 137, 245, 0.2);
  background: rgba(45, 81, 184, 0.1);
}

.hydro-item:last-child {
  margin-bottom: 0;
}

.hydro-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  color: #fff;
}

.hydro-icon i {
  font-size: 14px;
}

.water-icon {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
}

.visibility-icon {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
}

.wind-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.flowmeter-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.hydro-content {
  flex: 1;
  min-width: 0;
}

.hydro-label {
  font-size: 12px;
  color: #b8ccff;
  margin-bottom: 4px;
}

.hydro-values {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.hydro-value {
  font-size: 12px;
  color: #d8fbff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hydro-status {
  margin-left: 8px;
  flex-shrink: 0;
}

.hydro-status .status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.hydro-status.status-normal .status-dot {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.hydro-status.status-error .status-dot {
  background: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

.hydro-status.status-disabled .status-dot {
  background: #9ca3af;
  box-shadow: 0 0 0 2px rgba(156, 163, 175, 0.2);
}

.online-rate-body {
  height: calc(100% - 30px);
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 4px 6px;
}

.online-rate-top-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.online-rate-top-item {
  border: 1px solid rgba(101, 138, 247, 0.3);
  background: linear-gradient(180deg, rgba(49, 85, 187, 0.3) 0%, rgba(34, 62, 146, 0.35) 100%);
  border-radius: 8px;
  padding: 6px 8px;
}

.top-item-label {
  font-size: 13x;
  color: #a9c4ff;
  line-height: 1;
}

.top-item-value {
  margin-top: 6px;
  font-size: 20px;
  line-height: 1;
  font-weight: 700;
  color: #d8fbff;
}

.top-item-value.online {
  color: #34d399;
}

.top-item-value.offline {
  color: #f59e0b;
}

.online-rate-main {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px 0;
}

.online-rate-chart-wrap {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.online-rate-progress-center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: transparent;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  box-sizing: border-box;
}

.online-rate-progress {
  transform: translateZ(0);
  opacity: 0.85
}

.online-rate-progress ::v-deep .el-progress-circle__track {
  stroke: rgba(120, 136, 164, 0.36);
}

.online-rate-progress ::v-deep .el-progress-circle__path {
  transition: stroke-dasharray 0.6s ease;
}

.online-rate-value {
  font-weight: 700;
  color: rgba(232, 238, 249, 0.95);
  line-height: 1;
  letter-spacing: 0.2px;
  text-shadow: none;
}

.online-rate-label {
  color: rgba(200, 213, 236, 0.9);
}

.list-row {
  display: grid;
  grid-template-columns: minmax(90px, 1.2fr) minmax(100px, 1fr) minmax(120px, 1.2fr);
  gap: 8px;
  padding: 7px 6px;
  font-size: 12px;
  border-bottom: 1px solid rgba(102, 137, 245, 0.2);
  background: rgba(45, 81, 184, 0.14);
}

.list-head {
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 12px;
  font-weight: 600;
  color: #d7e4ff;
  border-bottom: 1px solid rgba(102, 137, 245, 0.34);
  background: linear-gradient(180deg, rgba(43, 80, 183, 0.6) 0%, rgba(27, 54, 128, 0.68) 100%);
}

.list-row:nth-child(2n) {
  background: rgba(45, 81, 184, 0.18);
}

.list-row:hover {
  background: rgba(77, 116, 236, 0.24);
}

.row-name,
.row-type,
.row-time {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device-list-row {
  grid-template-columns: minmax(90px, 1fr) minmax(90px, 0.8fr) minmax(110px, 1fr) minmax(70px, 0.7fr);
}

.row-status {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.screen-bottom {
  height: 300px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
}

.bottom-safe-gap {
  height: 0;
  flex-shrink: 0;
}

.table-wrap {
  height: calc(100% - 30px);
  overflow: auto;
  padding-right: 2px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

th,
td {
  border-bottom: 1px solid rgba(0, 219, 255, 0.16);
  padding: 6px;
  text-align: left;
}

th {
  position: sticky;
  top: 0;
  z-index: 1;
  color: #d7e4ff;
  font-weight: 600;
  background: linear-gradient(180deg, rgba(36, 70, 162, 0.9) 0%, rgba(21, 44, 110, 0.92) 100%);
}

tbody tr:nth-child(2n) {
  background: rgba(45, 81, 184, 0.16);
}

tbody tr:hover {
  background: rgba(77, 116, 236, 0.24);
}

.empty-text {
  padding: 12px 0;
  color: #84a9c7;
  font-size: 12px;
  text-align: center;
}

.list-body::-webkit-scrollbar,
.table-wrap::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.overlay-layer ::v-deep .panel-loading-mask {
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-layer ::v-deep .panel-loading-mask .el-loading-spinner {
  position: static;
  margin-top: 0;
  transform: none;
  width: auto;
  text-align: center;
}

.list-body::-webkit-scrollbar-track,
.table-wrap::-webkit-scrollbar-track {
  background: rgba(7, 39, 76, 0.4);
}

.list-body::-webkit-scrollbar-thumb,
.table-wrap::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: rgba(71, 212, 255, 0.55);
}

@media (max-width: 1680px) {
  .screen-header {
    left: 368px;
    right: 368px;
  }

  .screen-main {
    grid-template-columns: 350px 1fr 350px;
  }

  .header-title {
    font-size: 36px;
    letter-spacing: 6px;
  }

  .header-subline {
    font-size: 11px;
    width: min(84%, 860px);
  }

  .screen-bottom {
    height: 236px;
  }
}
</style> 